import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import { Box, Button, FlexLayout, H2, Icon } from '@deity-io/portal-ui';
import CreateEditVariableModal from 'components/Package/VariablesTab/CreateEditVariableModal';
import VariableWrapper from 'components/Package/VariablesTab/VariableWrapper';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import _ from 'lodash';
import { useState } from 'react';
import type { FC } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GetPackageVersionDocument,
  useGetPackageVersionQuery,
  useUpdatePackageVersionVariablesMutation
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

interface VariablesTabProps {
  locked?: boolean;
}
export const VariablesTab: FC<VariablesTabProps> = ({ locked }) => {
  const { pathname } = useLocation();
  const params: { [key: string]: string } = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: packageVersionData } = useGetPackageVersionQuery({
    variables: {
      uuid: params.versionUuid
    },
    skip: !params.versionUuid,
    ...CACHE_FIRST
  });

  const [update, { loading }] = useUpdatePackageVersionVariablesMutation({
    refetchQueries: [
      {
        query: GetPackageVersionDocument,
        variables: {
          uuid: params.versionUuid
        }
      }
    ]
  });
  const variables: IPropertyBag = {};
  mapRecord(packageVersionData?.getPackageVersion?.variables, (environmentSet, environment) => {
    mapRecord(environmentSet as IPropertyBag, (variable, variableKey) => {
      _.set(variables, [variableKey, environment], variable);

      return undefined;
    });

    return undefined;
  });

  const rename = (owner: IPropertyBag, oldKey: string, newKey: string): IPropertyBag => {
    const output: IPropertyBag = {};
    mapRecord(owner, (item, key) => {
      if (key === oldKey) {
        output[newKey] = item;
      } else {
        output[key] = item;
      }

      return undefined;
    });

    return output;
  };

  const updateVariable = (key: string, value: IPropertyBag | undefined) => {
    let newVariables = _.cloneDeep(variables);
    let newKey: string | undefined;
    if (value) {
      mapRecord(value, (variable, env) => {
        if (env !== '_key') {
          _.set(newVariables, [key, env], variable);
        } else {
          newKey = variable;
        }
      });
    } else {
      _.unset(newVariables, [key]);
    }
    if (newKey) {
      newVariables = rename(newVariables, key, newKey);
    }

    const output: IPropertyBag = {};
    mapRecord(newVariables, (envList, variableKey) => {
      mapRecord(envList, (outputValue, env) => {
        _.set(output, [env, variableKey], outputValue);
      });
    });
    update({ variables: { uuid: params.versionUuid, variables: output } });
  };

  const prefix = `var_`;
  let maxCounter = 0;
  mapRecord(variables, (list, key) => {
    const matches = key.match(`^${prefix}(\\d+)$`);
    if (matches && matches[1]) {
      const found = Number.parseInt(matches[1], 10);
      if (found > maxCounter) {
        maxCounter = found;
      }
    }
  });
  let maxString = `${maxCounter + 1}`;
  while (maxString.length < 3) {
    maxString = `0${maxString}`;
  }
  const newVariableKey = prefix + maxString;

  let variableCount = 0;

  return params?.packageId && params?.versionUuid ? (
    <>
      <Box p={7}>
        <FlexLayout justifyContent="space-between" alignItems="center" sx={{ pb: 5 }}>
          <H2>Variables</H2>
          {!locked && (
            <Button variant="outlined" startIcon={<Icon icon="add" />} onClick={() => setIsModalOpen(true)}>
              New variable
            </Button>
          )}
        </FlexLayout>

        {mapRecordToArray(variables, (variable, variableKey) => {
          // eslint-disable-next-line no-plusplus
          variableCount++;

          return (
            <VariableWrapper
              key={variableKey}
              variableKey={variableKey}
              variable={variable as IPropertyBag}
              locked={locked}
              onUpdate={updateVariable}
            />
          );
        })}
        {variableCount === 0 && (
          <FlexLayout justifyContent="center">No variables yet for this package version.</FlexLayout>
        )}
      </Box>
      {isModalOpen && (
        <CreateEditVariableModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          variableKey={newVariableKey}
          onUpdate={updateVariable}
        />
      )}
    </>
  ) : null;
};
